import { useState, useCallback, useEffect } from 'react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import ModalSmsFreeDeposit from '~/components/modals/sms/ModalSmsFreeDeposit';
import { ClinicModel } from '../../model/Clinic/ClinicModel';
import { SmsDataTable } from './DataTable';
import Pagination from '../../app/Pagination';
import { useApp } from '~/hooks/useApp';
import { format, subDays } from 'date-fns';
import { Tooltip } from '~/components/commons/Tooltip';
import { color } from '~/theme';
import { FilterDateRange } from '~/components/commons/SearchFilterDateRange';
import PageNav from '~/components/commons/PageNav';
import { useApi } from '~/components/providers/ApiProvider';
import { useModal } from '~/hooks/useModal';
import { useSnackbarContext } from '~/components/providers/SnackbarProvider';
import {
  Box,
  ButtonContainer,
  DropdownList,
  TextField,
  Typography,
} from './SmsPointManagements.styled';
import ModalChargeRefund from '~/components/modals/sms/ModalChargeRefund';

type StatusFilter =
  | 'depositRequest'
  | 'depositComplete'
  | 'refundRequest'
  | 'refundComplete'
  | 'freeDeposit'
  | 'freeRefund'
  | 'paidDeposit'
  | 'paidRefund';

const statusFilterOptions: { label: string; value: StatusFilter }[] = [
  { label: '충전신청', value: 'depositRequest' },
  { label: '충전완료', value: 'depositComplete' },
  { label: '환불신청', value: 'refundRequest' },
  { label: '환불완료', value: 'refundComplete' },
  { label: '무료충전', value: 'freeDeposit' },
  { label: '무료환불', value: 'freeRefund' },
  { label: '유료충전', value: 'paidDeposit' },
  { label: '유료환불', value: 'paidRefund' },
];

const toOptionalParam = (value: any) => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || undefined;
  }
  return value;
};

const SmsPointManagements = () => {
  const app = useApp();
  const { excelDownloadApi } = useApi();
  const modal = useModal();
  const snackbar = useSnackbarContext();
  const defaultParams = app.$qb().limit(20).orderBy('id desc');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [params, setParams] = useState<any>(defaultParams);
  const [searchValue, setSearchValue] = useState<any>({});
  const [startAt, setStartAt] = useState<Date | null>(subDays(new Date(), 30));
  const [endAt, setEndAt] = useState<Date | null>(new Date());
  const [completeStartAt, setCompleteStartAt] = useState<Date | null>(null);
  const [completeEndAt, setCompleteEndAt] = useState<Date | null>(null);
  const [limit] = useState(params ? params.queryParams.limit : 10);
  const [currentIndex, setCurrentIndex] = useState(0);

  const callApi = useCallback(
    async (nextParams = params) => {
      try {
        const response: any = await app.$smsRepo.sms.all({
          ...nextParams.queryParams,
        });
        if (response?.data) {
          setData(response.data);
        }
        if (response?.pagination) {
          setTotal(response.pagination.total);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [params, app.$smsRepo.sms]
  );

  const handleClickChargeRefund = async () => {
    const clinicData = ClinicModel.create({});
    clinicData.setData(await app.$clinicRepo.clinic.namesAll());

    runInAction(() => {
      app.$modal.custom({
        component: ModalChargeRefund,
        options: {
          clinics: clinicData.clinicList,
          callApi,
        },
      });
    });
  };

  const handleChangeSearch = (name: string, value: string) => {
    const searchParam = { ...searchValue, [name]: value };
    setSearchValue(searchParam);
  };

  const getSmsPointFilterParams = () => {
    const normalizeParams = (params: Record<string, any>) =>
      Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
          key,
          toOptionalParam(value),
        ])
      );

    const formatDateParam = (date?: Date | null) =>
      date ? format(new Date(date), 'yyyy-MM-dd') : undefined;

    return normalizeParams({
      ...params.queryParams,
      startDateAt: formatDateParam(startAt),
      endDateAt: formatDateParam(endAt),
      completeStartAt: formatDateParam(completeStartAt),
      completeEndAt: formatDateParam(completeEndAt),
      status: searchValue.status,
      name: searchValue.name,
      employerNo: searchValue.employerNo,
      acctNm: searchValue.acctNm,
      creatorName: searchValue.creatorName,
    });
  };

  const handleClickSearch = () => {
    setCurrentIndex(0);
    const editParams = {
      ...params,
      queryParams: {
        ...getSmsPointFilterParams(),
        page: 1,
      },
    };
    setParams(editParams);
    callApi(editParams);
  };

  const isSearchDisabled =
    startAt === null &&
    endAt === null &&
    completeStartAt === null &&
    completeEndAt === null &&
    Object.values(searchValue).every((value: any) => !value);

  const handleChangeStartAt = (date: Date | null) => {
    setStartAt(date);
  };

  const handleChangeEndAt = (date: Date | null) => {
    setEndAt(date);
  };

  const handleChangeCompleteStartAt = (date: Date | null) => {
    setCompleteStartAt(date);
  };

  const handleChangeCompleteEndAt = (date: Date | null) => {
    setCompleteEndAt(date);
  };

  const handlePage = (idx: number) => {
    if (!params) return;
    if (idx >= 0) {
      setCurrentIndex(idx);
    }
    const nextParams = {
      ...params,
      queryParams: {
        ...params.queryParams,
        page: idx + 1,
      },
    };
    setParams(nextParams);
    callApi(nextParams);
  };

  const handleFailExcelDownload = () => {
    modal.close({
      type: 'LOADING',
    });
    snackbar.alert('다운로드를 실패했습니다.');
  };

  const downloadFile = async (location: string) => {
    modal.close({
      type: 'LOADING',
    });

    const res: any = await excelDownloadApi.downloadExcel(
      location,
      '문자충전_관리.xlsx'
    );

    const link = document.createElement('a');
    link.href = res.data.url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExcelDownload = async () => {
    modal.loading({
      type: 'LOADING',
      msg: '다운로드중입니다. 데이터 양에 따라 \n 수 초 ~ 수 분이 걸릴 수 있습니다.',
    });

    const resp: any = await excelDownloadApi.postExcelsSms({
      filters: getSmsPointFilterParams(),
      reason: 'temp1234!',
      password: 'tempf1234!',
    });

    if (resp?.data) {
      const taskId: number = resp.data.id;
      const startInterval = setInterval(async () => {
        try {
          const res: any = await excelDownloadApi.getTasks(taskId);
          switch (res.data.status) {
            case 'SUCCESS':
              clearInterval(startInterval);
              downloadFile(res.data.location);
              break;
            case 'FAILURE':
              clearInterval(startInterval);
              handleFailExcelDownload();
              break;
          }
        } catch (e) {
          clearInterval(startInterval);
          handleFailExcelDownload();
        }
      }, 2000);
    } else {
      handleFailExcelDownload();
    }
  };

  return (
    <div className="main-contents">
      <PageNav pages={['SMS', `문자충전 관리 (${total})`]} />
      <div className="search-bar">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${color.common.line}`,
                '& > *:not(:last-child)': {
                  borderRight: `1px solid ${color.common.line}`,
                },
              }}
            >
              <Box width={380} className="bg-white h-full">
                <FilterDateRange
                  label="충전신청일"
                  startAt={startAt || undefined}
                  endAt={endAt || undefined}
                  placeholder={'YYYY-MM-DD'}
                  onChange={(start: string, end: string) => {
                    handleChangeStartAt(start ? new Date(start) : null);
                    handleChangeEndAt(end ? new Date(end) : null);
                  }}
                />
              </Box>
              <Box width={380} className="bg-white h-full">
                <FilterDateRange
                  label="입금일"
                  startAt={completeStartAt || undefined}
                  endAt={completeEndAt || undefined}
                  placeholder={'YYYY-MM-DD'}
                  onChange={(start: string, end: string) => {
                    handleChangeCompleteStartAt(start ? new Date(start) : null);
                    handleChangeCompleteEndAt(end ? new Date(end) : null);
                  }}
                />
              </Box>

              <Box width={210}>
                <DropdownList
                  variant="search"
                  startAdornment={<Typography>상태</Typography>}
                  placeholder="전체"
                  value={statusFilterOptions.filter(
                    (option) => option.value === searchValue?.status
                  )}
                  options={statusFilterOptions}
                  getOptionLabel={(option: any) => {
                    if (option[0]) {
                      return option[0]?.label ? option[0].label : '';
                    }
                    return option ? option.label || '' : '';
                  }}
                  onChange={(option: any) => {
                    handleChangeSearch('status', option?.value || '');
                  }}
                />
              </Box>

              <Box width={210}>
                <TextField
                  $type="search"
                  value={searchValue?.['name'] || ''}
                  startAdornment="병원명"
                  placeholder="입력"
                  onChange={(v: any) => {
                    handleChangeSearch('name', v);
                  }}
                  onEnter={handleClickSearch}
                />
              </Box>

              <Box width={210}>
                <TextField
                  $type="search"
                  value={searchValue?.['employerNo'] || ''}
                  startAdornment="사업자번호"
                  placeholder="입력"
                  onChange={(v: any) => {
                    handleChangeSearch('employerNo', v);
                  }}
                  onEnter={handleClickSearch}
                />
              </Box>

              <Box width={210}>
                <TextField
                  $type="search"
                  value={searchValue?.['acctNm'] || ''}
                  startAdornment="입금자명"
                  placeholder="입력"
                  onChange={(v: any) => {
                    handleChangeSearch('acctNm', v);
                  }}
                  onEnter={handleClickSearch}
                />
              </Box>

              <Box width={210}>
                <TextField
                  $type="search"
                  value={searchValue?.['creatorName'] || ''}
                  startAdornment="작업자"
                  placeholder="입력"
                  onChange={(v: any) => {
                    handleChangeSearch('creatorName', v);
                  }}
                  onEnter={handleClickSearch}
                />
              </Box>
            </Box>
            <div className="search-btns" style={{ marginLeft: '8px' }}>
              <button
                className="btn btn-black"
                onClick={handleClickSearch}
                disabled={isSearchDisabled}
              >
                {app.$translate('FIND')}
              </button>
            </div>
          </div>

          <ButtonContainer>
            <Tooltip
              title={app.$me.role === 'manager' ? '권한이 없습니다.' : ''}
            >
              <button
                className="btn btn-black btn-m bold"
                onClick={handleClickChargeRefund}
                disabled={app.$me.role === 'manager'}
              >
                충전/환불 관리
              </button>
            </Tooltip>

            <button
              className="btn btn-white btn-m bold"
              onClick={handleExcelDownload}
            >
              EXCEL 내려받기
            </button>
          </ButtonContainer>
        </Box>
      </div>

      <SmsDataTable items={data} callApi={callApi} />
      <div className="flex-row flex-between wrap-pagination">
        <Pagination
          total={total}
          limit={limit}
          currentIndex={currentIndex}
          onPage={handlePage}
        />
      </div>
    </div>
  );
};

export default observer(SmsPointManagements);
